import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../utility/axios';
import useSound from 'use-sound';
import notificationSound from '../assets/sounds/notification_sound_1.mp3';
import { useTableChanges } from './useWebSocket';

// Notification sound - using local file
const NOTIFICATION_SOUND = notificationSound;

export const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const previousCountRef = useRef(0);
    const isFirstLoadRef = useRef(true);
    
    // Initialize notification sound
    const [playNotificationSound] = useSound(NOTIFICATION_SOUND, {
        volume: 0.5,
        interrupt: true
    });

    const fetchNotifications = useCallback(async (silent = false) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const response = await axios.get('/api/get_notifications/');
            
            if (response.data.notification_data) {
                const newNotifications = response.data.notification_data;
                const newUnreadCount = newNotifications.filter(n => !n.had_been_read).length;
                
                // Play sound if there are new unread notifications
                if ((isFirstLoadRef.current && newUnreadCount > 0) ||
                    (!isFirstLoadRef.current && newUnreadCount > previousCountRef.current)) {
                    playNotificationSound();
                }
                
                previousCountRef.current = newUnreadCount;
                isFirstLoadRef.current = false;
                setNotifications(newNotifications);
            } else {
                setNotifications([]);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError(err.response?.data?.error || 'Failed to load notifications');
            setNotifications([]);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [playNotificationSound]);

    const deleteNotification = async (notificationId) => {
        try {
            const response = await axios.delete(`/api/delete_notification/${notificationId}/`);
            
            if (response.status === 200) {
                // Remove the notification from the local state
                setNotifications(prev => {
                    const updated = prev.filter(notification => notification.id !== notificationId);
                    // Update the previous count
                    previousCountRef.current = updated.filter(n => !n.had_been_read).length;
                    return updated;
                });
                return { success: true };
            } else {
                return { success: false, error: 'Failed to delete notification' };
            }
        } catch (err) {
            console.error('Error deleting notification:', err);
            return { success: false, error: err.response?.data?.error || 'Failed to delete notification' };
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const response = await axios.post(`/api/update_notification/${notificationId}/`, {
                had_been_read: true
            });
            
            if (response.status === 200) {
                // Update the notification in the local state
                setNotifications(prev => {
                    const updated = prev.map(notification => 
                        notification.id === notificationId 
                            ? { ...notification, had_been_read: true }
                            : notification
                    );
                    // Update the previous count
                    previousCountRef.current = updated.filter(n => !n.had_been_read).length;
                    return updated;
                });
                return { success: true };
            } else {
                return { success: false, error: 'Failed to mark notification as read' };
            }
        } catch (err) {
            console.error('Error marking notification as read:', err);
            return { success: false, error: err.response?.data?.error || 'Failed to mark notification as read' };
        }
    };

    // Handle real-time notification changes
    const handleNotificationChange = useCallback((change) => {
        if (change.operation === 'INSERT') {
            // New notification - play sound and refresh
            playNotificationSound();
            fetchNotifications(true);
        } else if (change.operation === 'UPDATE') {
            // Notification updated - refresh silently
            fetchNotifications(true);
        } else if (change.operation === 'DELETE') {
            // Notification deleted - remove from local state
            if (change.data?.id) {
                setNotifications(prev => {
                    const updated = prev.filter(n => n.id !== change.data.id);
                    previousCountRef.current = updated.filter(n => !n.had_been_read).length;
                    return updated;
                });
            }
        }
    }, [fetchNotifications, playNotificationSound]);

    // Subscribe to real-time notifications table changes
    const { isConnected } = useTableChanges('notifications', handleNotificationChange);

    useEffect(() => {
        // Initial fetch
        fetchNotifications();
    }, [fetchNotifications]);

    return { 
        notifications, 
        loading, 
        error, 
        fetchNotifications,
        deleteNotification,
        markAsRead,
        unreadCount: notifications.filter(n => !n.had_been_read).length,
        isRealtimeConnected: isConnected
    };
};
